/**
 * Utilities for parsing and displaying Python code snippets in the Code tab
 * of the DataFrameAnalysis artifact, plus shared icon helpers for artifact pills.
 */
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType } from "build-ts/common";
import { AppIcons } from "constants/icons";
import { getArtifactType } from "modules/artifacts/utils/artifactUtils";
import { coy } from "react-syntax-highlighter/dist/esm/styles/prism";

/**
 * Returns the correct icon for an artifact referenced by name in a parent-header pill.
 * - KG_DATAFRAME  → database icon
 * - chart sub-resource inside a DATAFRAME_ANALYSIS → chart icon
 * - report sub-resource inside a DATAFRAME_ANALYSIS → document icon
 * - anything else (pandas df, unknown) → dataframe icon
 */
export function getParentPillIcon(
  name: string | undefined,
  lookupFn: (name: string) => ArtifactProto | undefined
): React.ElementType {
  const artifact = lookupFn(name ?? "");
  const type = artifact ? getArtifactType(artifact) : null;
  if (type === ArtifactType.KG_DATAFRAME) return AppIcons.database;
  const dfa = artifact?.dataframe_analysis;
  if ((dfa?.charts ?? []).some(c => c.name === name)) return AppIcons.chart;
  if ((dfa?.reports ?? []).some(r => r.name === name)) return AppIcons.document;
  return AppIcons.dataframe;
}

/** Const object for tab IDs — use these instead of raw string literals. */
export const TAB_ID = {
  results: "results",
  code: "code",
  inputs: "inputs",
  trace: "trace",
} as const;

export type TabId = (typeof TAB_ID)[keyof typeof TAB_ID];
export const VALID_TABS: TabId[] = [TAB_ID.results, TAB_ID.code, TAB_ID.inputs, TAB_ID.trace];

/** Const object for result pill kinds — use these instead of raw string literals. */
export const RESULT_PILL_KIND = {
  dataframe: "dataframe",
  chart: "chart",
  report: "report",
} as const;

export const CODE_TAB_GUTTER_SLOT_W = 8;

export function isValidTabId(tab?: string): tab is TabId {
  return !!tab && VALID_TABS.includes(tab as TabId);
}

/** True when the line is a Python full-line # comment (optional indent, then #). */
export const isHashCommentLine = (line: string) => /^\s*#/.test(line);

/** Display-only: hide leading `#` / `##` and spacing so comments read as prose. */
export function stripHashCommentDisplay(line: string): string {
  if (!isHashCommentLine(line)) return line;
  return line.replace(/^\s*#+\s*/, "");
}

/**
 * Split on full-line # blocks: each section = consecutive # lines + following code until the next # block.
 * Code before any # is one section with empty `comment`.
 */
export function splitPythonIntoCommentLedSections(source: string): { comment: string; code: string }[] {
  const lines = source.split("\n");
  const out: { comment: string; code: string }[] = [];
  let cbuf: string[] = [];
  let xbuf: string[] = [];
  const hasCode = () => xbuf.join("\n").trim().length > 0;

  for (const line of lines) {
    if (/^\s*#/.test(line)) {
      if (hasCode()) {
        out.push({ comment: cbuf.join("\n"), code: xbuf.join("\n") });
        cbuf = [line];
        xbuf = [];
      } else {
        cbuf.push(line);
      }
    } else {
      xbuf.push(line);
    }
  }
  if (cbuf.join("\n").trim().length > 0 || hasCode()) {
    out.push({ comment: cbuf.join("\n"), code: xbuf.join("\n") });
  }
  return out;
}

/** Lines of code with real content — ignores blank lines. */
export const countCodeLines = (raw: string): number => {
  if (!raw.trim()) return 0;
  return raw.split(/\r?\n/).filter(line => line.trim().length > 0).length;
};

/** Leading section: code before the first full-line `#` — shown inline, not in expand/collapse list. */
export function isPythonPrologueSection(sec: { comment: string; code: string }, i: number): boolean {
  return i === 0 && !sec.comment.trim() && countCodeLines(sec.code) > 0;
}

/** Section indices that get per-block expand/collapse in the Code tab. */
export function getCollapsiblePythonCodeSectionIndices(source: string): number[] {
  if (!source.trim()) return [];
  const sections = splitPythonIntoCommentLedSections(source);
  return sections
    .map((s, i) => {
      if (countCodeLines(s.code) <= 0) return null;
      if (isPythonPrologueSection(s, i)) return null;
      return i;
    })
    .filter((x): x is number => x !== null);
}

/** Split source into lines without duplicating terminal newline as an extra blank row. */
export function splitIntoDisplayLines(source: string): string[] {
  const normalized = source.replace(/\r/g, "");
  let lines = normalized.split("\n");
  if (lines.length > 1 && lines[lines.length - 1] === "") {
    lines = lines.slice(0, -1);
  }
  return lines;
}

const PYTHON_COMMENT_COLOR = "#4e1e6c";

/** oneLight with darker purple `#` comments for the code tab. */
export const PYTHON_CODE_STYLE = {
  ...coy,
  comment: { ...coy.comment, color: PYTHON_COMMENT_COLOR },
  prolog: { ...coy.prolog, color: PYTHON_COMMENT_COLOR },
  cdata: { ...coy.cdata, color: PYTHON_COMMENT_COLOR },
};
